/**
 * PATH: packages/email-hub/src/feature/signal-strip/ReaderSignalStripMenus.tsx
 * FILE_ID: EMAILHUB-FEATURE-SIGNALSTRIP-MENUS-001
 * PURPOSE: Lane-specific keyboard/touch kebab menus for reader signals.
 * WHY: A generic email menu cannot say whether an action changes Company,
 *      Classification, Intelligence, or Filed to. This frame owns that
 *      semantic menu boundary while ReaderSignalStrip stays an assembler.
 *      BUG-2026-07-17-EMAIL-LANE-DEAD-CONTEXTMENU: never render a disabled-only
 *      Intelligence stub — Ask AI is always available on that lane.
 * RELATED: ReaderSignalStrip.tsx, SignalStrip.tsx, laneMenuActions.ts
 */
import { type MouseEvent } from 'react';
import { Briefcase, Building2, ClipboardCopy, MoreHorizontal, Sparkles, Wand2 } from 'lucide-react';

import {
  ContextMenu,
  ContextMenuHeader,
  ContextMenuItem,
  ContextMenuSeparator,
} from '../../_internal/components/ContextMenu';
import { useToast } from '../../_internal/components/Toast';
import type { Email } from '../hooks/useEmails';
import { rejectVerificationSignal, type EmailActionSignal } from '../action-signals';
import type { ReaderSignalLane } from './laneMenuActions';

export type { ReaderSignalLane };

export interface LaneMenuPosition {
  lane: ReaderSignalLane;
  x: number;
  y: number;
  trigger: HTMLElement;
}

export function ReaderSignalStripMenuButton({
  lane,
  label,
  onOpen,
}: {
  lane: ReaderSignalLane;
  label: string;
  onOpen: (position: LaneMenuPosition) => void;
}) {
  return (
    <button
      type="button"
      onClick={(event: MouseEvent<HTMLButtonElement>) => {
        const rect = event.currentTarget.getBoundingClientRect();
        onOpen({
          lane,
          x: rect.left,
          y: rect.bottom,
          trigger: event.currentTarget,
        });
      }}
      aria-label={label}
      title={label}
      className="inline-flex min-h-11 min-w-11 touch-manipulation items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:min-h-7 sm:min-w-7"
    >
      <MoreHorizontal className="h-3.5 w-3.5" aria-hidden />
    </button>
  );
}

export interface ReaderSignalStripMenusProps {
  menu: LaneMenuPosition | null;
  email: Email;
  verificationCode: EmailActionSignal | null;
  verificationDismissed: boolean;
  onClose: () => void;
  onOpenCompanyContext: () => void;
  onShowCompanyEmails?: () => void;
  onOpenCard?: (cardId: string) => void;
  onOpenBoard?: () => void;
  canRequestRule: boolean;
  onRequestRule: () => void;
  onAskAi: () => void;
  onDismissVerification: () => void;
}

export function ReaderSignalStripMenus({
  menu,
  email,
  verificationCode,
  verificationDismissed,
  onClose,
  onOpenCompanyContext,
  onShowCompanyEmails,
  onOpenCard,
  onOpenBoard,
  canRequestRule,
  onRequestRule,
  onAskAi,
  onDismissVerification,
}: ReaderSignalStripMenusProps) {
  const { toast } = useToast();
  if (!menu) return null;

  const copyText = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast(`Copied ${label}`, 'success');
    } catch {
      toast(`Couldn't copy ${label} — your browser blocked clipboard access.`, 'error');
    }
  };

  const heading = {
    who: 'Company',
    what: 'Classification',
    signals: 'Intelligence',
    where: 'Filed to',
  }[menu.lane];
  const filedCardId = email.allocation_summary?.card_id;
  const filedBoardId = email.allocation_summary?.board_id;
  const hasActiveVerification = Boolean(verificationCode && !verificationDismissed);

  return (
    <ContextMenu
      x={menu.x}
      y={menu.y}
      label={`${heading} actions`}
      restoreFocusTo={menu.trigger}
      onClose={onClose}
    >
      <ContextMenuHeader>{heading}</ContextMenuHeader>
      {menu.lane === 'who' && (
        <>
          {email.company_domain ? (
            <ContextMenuItem
              icon={Building2}
              onClick={() => {
                onClose();
                onOpenCompanyContext();
              }}
            >
              Open company context
            </ContextMenuItem>
          ) : (
            <ContextMenuItem icon={Building2} disabled>
              Company context unavailable
            </ContextMenuItem>
          )}
          {onShowCompanyEmails && (
            <ContextMenuItem
              icon={Briefcase}
              onClick={() => {
                onClose();
                onShowCompanyEmails();
              }}
            >
              Show emails from this company
            </ContextMenuItem>
          )}
          <ContextMenuSeparator />
          <ContextMenuItem
            icon={ClipboardCopy}
            onClick={() => {
              onClose();
              void copyText(email.company_label || email.company_domain || '', 'company name');
            }}
          >
            Copy company name
          </ContextMenuItem>
          {email.company_domain && (
            <ContextMenuItem
              icon={ClipboardCopy}
              onClick={() => {
                onClose();
                void copyText(email.company_domain || '', 'company domain');
              }}
            >
              Copy domain
            </ContextMenuItem>
          )}
        </>
      )}
      {menu.lane === 'what' && (
        <>
          <ContextMenuItem
            icon={Sparkles}
            onClick={() => {
              onClose();
              onAskAi();
            }}
          >
            Ask AI about this classification
          </ContextMenuItem>
          {canRequestRule && (
            <ContextMenuItem
              icon={Wand2}
              onClick={() => {
                onClose();
                onRequestRule();
              }}
            >
              Request a classification rule
            </ContextMenuItem>
          )}
        </>
      )}
      {menu.lane === 'signals' && (
        <>
          <ContextMenuItem
            icon={Sparkles}
            onClick={() => {
              onClose();
              onAskAi();
            }}
          >
            Ask AI about these signals
          </ContextMenuItem>
          {hasActiveVerification && verificationCode && (
            <>
              <ContextMenuItem
                icon={ClipboardCopy}
                onClick={() => {
                  onClose();
                  void copyText(
                    verificationCode.display_value,
                    verificationCode.context.toLowerCase()
                  );
                }}
              >
                Copy verification code
              </ContextMenuItem>
              <ContextMenuItem
                icon={Sparkles}
                onClick={() => {
                  onClose();
                  void rejectVerificationSignal(email.id)
                    .then(() => {
                      onDismissVerification();
                      toast(
                        'Marked as not a verification code. The code itself was not saved.',
                        'success'
                      );
                    })
                    .catch(() => toast('Could not record that correction. Try again.', 'error'));
                }}
              >
                Not a code
              </ContextMenuItem>
            </>
          )}
          {Boolean(email.ai_summary?.trim()) && (
            <ContextMenuItem
              icon={ClipboardCopy}
              onClick={() => {
                onClose();
                void copyText(email.ai_summary || '', 'AI summary');
              }}
            >
              Copy AI summary
            </ContextMenuItem>
          )}
        </>
      )}
      {menu.lane === 'where' && (
        <>
          {filedCardId && onOpenCard && (
            <ContextMenuItem
              icon={Briefcase}
              onClick={() => {
                onClose();
                onOpenCard(filedCardId);
              }}
            >
              Open filed card
            </ContextMenuItem>
          )}
          {filedBoardId && onOpenBoard && (
            <ContextMenuItem
              icon={Building2}
              onClick={() => {
                onClose();
                onOpenBoard();
              }}
            >
              Open filed board
            </ContextMenuItem>
          )}
          <ContextMenuItem
            icon={Sparkles}
            onClick={() => {
              onClose();
              onAskAi();
            }}
          >
            Explain this filing
          </ContextMenuItem>
          {canRequestRule && (
            <ContextMenuItem
              icon={Wand2}
              onClick={() => {
                onClose();
                onRequestRule();
              }}
            >
              Request a filing rule
            </ContextMenuItem>
          )}
        </>
      )}
    </ContextMenu>
  );
}

export default ReaderSignalStripMenus;
